import type { iCategory } from 'types/category'
import { queryClient } from '@services/query'

export const CATEGORIES_CACHE_KEY = 'categories_cache_v1'
const CATEGORIES_CACHE_TTL_MS = 1000 * 60 * 60 * 24

const readCachedCategories = () => {
  try {
    const raw = localStorage.getItem(CATEGORIES_CACHE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as { timestamp: number; categories: iCategory[] }
    if (!parsed?.categories?.length) return null
    if (Date.now() - parsed.timestamp > CATEGORIES_CACHE_TTL_MS) {
      localStorage.removeItem(CATEGORIES_CACHE_KEY)
      return null
    }
    return parsed.categories
  } catch {
    return null
  }
}

let mirrored = false

// Exported for tests: the mirror is per page load, which a test file isn't.
export const resetCategoriesMirror = () => {
  mirrored = false
}

/**
 * Mirrors the categories into localStorage for later page loads. The list runs
 * close to a megabyte, so it is written once per page load however many
 * consumers ask, and each write refreshes the stored copy's age.
 */
export const cacheCategories = (categories: iCategory[]) => {
  if (!categories?.length || mirrored) return

  mirrored = true

  try {
    localStorage.setItem(CATEGORIES_CACHE_KEY, JSON.stringify({ timestamp: Date.now(), categories }))
  } catch {
    // Ignore storage errors (e.g. private mode)
  }
}

export type CategoriesCache = iCategory[] | { data: iCategory[] } | undefined

/**
 * Unwraps the categories cache, which holds either the bare list or an envelope
 * depending on which caller populated it first.
 */
export const toCategoryList = (cached: CategoriesCache) => {
  if (Array.isArray(cached)) return cached
  if (Array.isArray(cached?.data)) return cached.data
  return null
}

const getCachedCategories = () => toCategoryList(queryClient.getQueryData<CategoriesCache>(['categories']))

export const isCategoriesReady = () => {
  const categories = getCachedCategories()
  return !!categories?.length
}

type PendingEvent = { event: string; data: Record<string, any> | (() => Record<string, any>) }
const pendingCategoryEvents: PendingEvent[] = []
let categoriesUnsubscribe: (() => void) | null = null
let categoriesFetchPromise: Promise<void> | null = null
let categoriesFallbackTimer: ReturnType<typeof setTimeout> | null = null
const FALLBACK_TIMEOUT_MS = 1500

const fetchCategoriesOnce = () => {
  if (categoriesFetchPromise || typeof window === 'undefined') return categoriesFetchPromise

  categoriesFetchPromise = fetch('/api/categories')
    .then(async response => {
      if (!response.ok) {
        throw new Error(`Failed to fetch categories: ${response.statusText}`)
      }
      return response.json()
    })
    .then(data => {
      queryClient.setQueryData(['categories'], data)
    })
    .catch(() => {
      // ignore fetch errors; events will continue to wait
    })
    .finally(() => {
      categoriesFetchPromise = null
    })

  return categoriesFetchPromise
}

const flushPendingCategoryEvents = (logger: (event: string, data: Record<string, any>) => void, force: boolean = false) => {
  if (!pendingCategoryEvents.length) return
  if (!force && !isCategoriesReady()) return

  const toFlush = pendingCategoryEvents.splice(0, pendingCategoryEvents.length)
  toFlush.forEach(({ event, data }) => {
    const payload = typeof data === 'function' ? data() : data
    logger(event, payload)
  })

  if (categoriesFallbackTimer) {
    clearTimeout(categoriesFallbackTimer)
    categoriesFallbackTimer = null
  }

  if (categoriesUnsubscribe) {
    categoriesUnsubscribe()
    categoriesUnsubscribe = null
  }
}

const ensureCategoriesSubscription = (logger: (event: string, data: Record<string, any>) => void) => {
  if (categoriesUnsubscribe || typeof window === 'undefined') return

  const cache = queryClient.getQueryCache()
  categoriesUnsubscribe = cache.subscribe(() => {
    if (isCategoriesReady()) {
      flushPendingCategoryEvents(logger)
    }
  })
}

const scheduleFallbackFlush = (logger: (event: string, data: Record<string, any>) => void) => {
  if (categoriesFallbackTimer || typeof window === 'undefined') return
  categoriesFallbackTimer = setTimeout(() => {
    categoriesFallbackTimer = null
    flushPendingCategoryEvents(logger, true)
  }, FALLBACK_TIMEOUT_MS)
}

export const logWithCategoriesReady = (
  logger: (event: string, data: Record<string, any>) => void,
  event: string,
  data: Record<string, any> | (() => Record<string, any>) = {}
) => {
  if (isCategoriesReady()) {
    const payload = typeof data === 'function' ? data() : data
    logger(event, payload)
    return
  }

  pendingCategoryEvents.push({ event, data })
  fetchCategoriesOnce()
  ensureCategoriesSubscription(logger)
  scheduleFallbackFlush(logger)
}

/**
 * Filters out parent categories from a product's categories array,
 * returning only child categories (those with parent_hash !== null)
 */
export const getChildCategories = (categories: string[]): string[] => {
  if (!categories || categories.length === 0) return []

  // Get categories from React Query cache, then localStorage fallback
  const allCategories = getCachedCategories() || readCachedCategories()

  if (!allCategories || allCategories.length === 0) return []

  const categoryMap = new Map<string, iCategory>(allCategories.map((cat: iCategory) => [cat.name, cat]))

  return categories.filter(categoryName => {
    const categoryData = categoryMap.get(categoryName)
    if (!categoryData) return false
    return categoryData.parent_hash !== null || categoryData.parent > 0
  })
}
